"use client"

/**
 * The mandatory opening fund allocation step (IBA-170).
 *
 * Opening balances say WHERE a branch's money is — bank, cash, mobile money. This says what it is FOR. The
 * distinction is not bookkeeping pedantry: every expense is checked against a FUND balance, so a branch that
 * converts with money in the bank and every fund at zero can record income all year and never spend a franc
 * of its own opening money.
 *
 * The three figures the step is built around — total, allocated, remaining — are computed by the SERVER from
 * the ledger, never here. The client showing "Remaining: 0" while the backend disagreed would let a treasurer
 * press a button that then refuses, which is the one failure this screen exists to prevent.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { financeQueryKeys } from "@/features/finance/services/finance-query-keys"
import { financeSetupKey } from "@/features/finance/services/finance-setup-api"
import { http } from "@/shared/lib/api/http"

/** Whether a fund was created by the church or auto-linked to an income account. Mirrors the backend enum. */
export enum OpeningAllocationFundKind {
  Custom = "CUSTOM",
  SystemLinked = "SYSTEM_LINKED",
}

export type OpeningAllocationFund = {
  id: string
  name: string
  kind: OpeningAllocationFundKind
  /** Canonical decimal string (IBA-116) — money never travels as a JSON number. */
  amount: string
}

export type OpeningFundAllocationState = {
  branchId: string
  currency: string
  totalOpeningMoney: string
  allocated: string
  remaining: string
  /** True when nothing is left to allocate — the condition for completing setup. */
  isSatisfied: boolean
  /** False when the branch converted with no money: the step is then satisfied with nothing to do. */
  hasOpeningMoney: boolean
  /** True once the books are open. */
  isActivated: boolean
  /** Allocations freeze on activation; corrections afterwards are fund reallocations. */
  isEditable: boolean
  setupComplete: boolean
  funds: OpeningAllocationFund[]
}

export type SetOpeningFundAllocationsInput = {
  /** The WHOLE set. A partial update cannot express "and nothing else", which is what the balance needs. */
  allocations: Array<{ fundId: string; amount: string }>
}

export const openingFundAllocationsKey = (workspaceId: string | null, branchId: string | null) =>
  financeQueryKeys.openingFundAllocationsState(workspaceId, branchId)

export async function fetchOpeningFundAllocations(
  workspaceId: string,
  branchId: string,
): Promise<OpeningFundAllocationState> {
  const response = await http.get<OpeningFundAllocationState>("/finance/opening-fund-allocations/state", {
    params: { workspaceId, branchId, scope: "BRANCH" },
  })
  return response.data
}

export async function setOpeningFundAllocations(
  workspaceId: string,
  branchId: string,
  input: SetOpeningFundAllocationsInput,
): Promise<Pick<OpeningFundAllocationState, "totalOpeningMoney" | "allocated" | "remaining" | "isSatisfied">> {
  const response = await http.put("/finance/opening-fund-allocations", {
    workspaceId,
    branchId,
    scope: "BRANCH",
    ...input,
  })
  return response.data
}

export function useOpeningFundAllocationsQuery(workspaceId: string | null, branchId: string | null) {
  return useQuery({
    queryKey: openingFundAllocationsKey(workspaceId, branchId),
    queryFn: () => fetchOpeningFundAllocations(workspaceId as string, branchId as string),
    enabled: Boolean(workspaceId && branchId),
  })
}

export function useSetOpeningFundAllocationsMutation(workspaceId: string | null, branchId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: SetOpeningFundAllocationsInput) =>
      setOpeningFundAllocations(workspaceId as string, branchId as string, input),
    onSuccess: () => {
      // Re-read rather than patch: the server owns the arithmetic, and the whole point of this screen is that
      // its totals are the ledger's, not the form's.
      queryClient.invalidateQueries({ queryKey: openingFundAllocationsKey(workspaceId, branchId) })
      // Fund balances move with the allocation — these rows ARE the funds' opening balance.
      queryClient.invalidateQueries({ queryKey: financeQueryKeys.funds() })
      /*
       * And the SETUP status, which carries `openingAllocation.isSatisfied` — the server's verdict that the
       * Complete Finance Setup button is gated on. Without this the save succeeded, the step said
       * "Enregistré", and the button stayed dead until something unrelated happened to refetch: the wizard
       * was reading a verdict from before the save it had just made.
       */
      queryClient.invalidateQueries({ queryKey: financeSetupKey(workspaceId, branchId) })
    },
  })
}
